import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ModalPopup from "../../components/modal";
import { SelectPicker, CheckPicker } from "rsuite";
import Button from "../../components/button";
import { sortCommodityFirst } from "../../utils/helpers/commodityOrder";

const tabs = [
  "District Mapping",
  "User Access",
  "Notifications",
  "Data & Refresh",
  "Account",
] as const;

type Tab = (typeof tabs)[number];

const tabContent: Record<Tab, { title: string; description: string }> = {
  "District Mapping": {
    title: "District Mapping",
    description:
      "Configure and manage district-to-region mappings for reporting and allocation purposes.",
  },
  "User Access": {
    title: "User Access Management",
    description:
      "Manage user roles and assignments. Product Category Managers have admin access. Product Managers are assigned commodities. Field Commodity Managers are assigned districts.",
  },
  Notifications: {
    title: "Notification Preferences",
    description:
      "Configure notification settings and alert thresholds for users and modules.",
  },
  "Data & Refresh": {
    title: "Data & Refresh Settings",
    description:
      "Manage data refresh schedules, data sources, and pipeline configurations.",
  },
  Account: {
    title: "Account Settings",
    description:
      "View and manage your account information and preferences.",
  },
};

interface UserRow {
  name: string;
  email: string;
  role: string;
  assignments: string[];
}

const roleColors: Record<string, string> = {
  "Product Category Manager": "bg-[#e8f5e9] text-[#4a7c2e]",
  "Product Manager": "bg-[#fff8e1] text-[#8d6e00]",
  "Field Commodity Manager": "bg-[#e3f2fd] text-[#1565c0]",
};

const mockUsers: UserRow[] = [
  {
    name: "Sarah Mitchell",
    email: "s.mitchell@graybar.com",
    role: "Product Category Manager",
    assignments: ["Full access"],
  },
  {
    name: "James Thornton",
    email: "j.thornton@graybar.com",
    role: "Product Category Manager",
    assignments: ["Full access"],
  },
  {
    name: "Maria Rodriguez",
    email: "m.rodriguez@graybar.com",
    role: "Product Manager",
    assignments: ["Steel Conduit", "PVC Pipe"],
  },
  {
    name: "David Chen",
    email: "d.chen@graybar.com",
    role: "Product Manager",
    assignments: ["Copper Wire"],
  },
  {
    name: "Lisa Patel",
    email: "l.patel@graybar.com",
    role: "Product Manager",
    assignments: ["Steel Conduit"],
  },
  {
    name: "Kevin O'Brien",
    email: "k.obrien@graybar.com",
    role: "Product Manager",
    assignments: ["PVC Pipe", "Copper Wire"],
  },
];

const roleOptions = [
  "Product Category Manager",
  "Product Manager",
  "Field Commodity Manager",
];

const commodityOptions = [
  "Steel Conduit",
  "PVC Pipe",
  "Copper Wire",
  "Fiber Optic Cable",
  "Aluminum Wire",
];

const districtOptions = [
  "Northeast",
  "Southeast",
  "Midwest",
  "Southwest",
  "West",
  "Northwest",
];

const UserAccessTable = () => {
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [users, setUsers] = useState<UserRow[]>(mockUsers);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [editRole, setEditRole] = useState("");
  const [editCommodity, setEditCommodity] = useState<string[]>([]);
  const [editDistrict, setEditDistrict] = useState<string[]>([]);

  const handleCommodityChange = (values: string[]) => {
    if (values.includes("All") && !editCommodity.includes("All")) {
      setEditCommodity(["All", ...commodityOptions]);
    } else if (!values.includes("All") && editCommodity.includes("All")) {
      setEditCommodity([]);
    } else if (values.filter((v) => v !== "All").length === commodityOptions.length) {
      setEditCommodity(["All", ...commodityOptions]);
    } else {
      setEditCommodity(values.filter((v) => v !== "All"));
    }
  };

  const handleDistrictChange = (values: string[]) => {
    if (values.includes("All") && !editDistrict.includes("All")) {
      setEditDistrict(["All", ...districtOptions]);
    } else if (!values.includes("All") && editDistrict.includes("All")) {
      setEditDistrict([]);
    } else if (values.filter((v) => v !== "All").length === districtOptions.length) {
      setEditDistrict(["All", ...districtOptions]);
    } else {
      setEditDistrict(values.filter((v) => v !== "All"));
    }
  };

  const handleEditClick = (user: UserRow) => {
    setEditingUser(user);
    setEditRole(user.role);
    setEditCommodity(user.assignments);
    setEditDistrict([]);
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    if (!editingUser) return;
    const assignments = editCommodity.filter((v) => v !== "All");
    setUsers((prev) =>
      prev.map((u) =>
        u.email === editingUser.email
          ? { ...u, role: editRole, assignments }
          : u
      )
    );
    toast.success(`Access updated for "${editingUser.name}".`);
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchName.toLowerCase()) &&
      user.email.toLowerCase().includes(searchEmail.toLowerCase())
  );

  const handleAddUser = () => {
    const trimmedName = searchName.trim();
    const trimmedEmail = searchEmail.trim();

    if (!trimmedName || !trimmedEmail) {
      return;
    }

    const alreadyExists = users.some(
      (u) => u.email.toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (alreadyExists) {
      toast.warning("User with this email already exists.");
      return;
    }

    const newUser: UserRow = {
      name: trimmedName,
      email: trimmedEmail,
      role: "Product Manager",
      assignments: [],
    };

    setUsers((prev) => [...prev, newUser]);
    setSearchName("");
    setSearchEmail("");
    toast.success(`User "${trimmedName}" added successfully.`);
  };

  const noMatch =
    searchName.trim().length > 0 &&
    searchEmail.trim().length > 0 &&
    filteredUsers.length === 0;

  return (
    <div className="mt-[2vh]">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="flex items-center gap-[1vw] mb-[2vh]">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="border border-[#CDCDCD] rounded px-[1vw] py-[1vh] text-fs14 w-[15vw] focus:outline-none focus:border-deloitteGreen"
        />
        <input
          type="text"
          placeholder="Search by email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className="border border-[#CDCDCD] rounded px-[1vw] py-[1vh] text-fs14 w-[15vw] focus:outline-none focus:border-deloitteGreen"
        />
        <button
          onClick={handleAddUser}
          disabled={!noMatch}
          className={`px-[1.2vw] py-[1vh] rounded text-fs14 font-semibold transition-colors ${
            noMatch
              ? "bg-deloitteGreen text-white hover:opacity-90 cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          + Add User
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-left text-[1.8vh] text-black">
          <thead>
            <tr className="bg-white">
              <th className="border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold text-[#333]">
                Name
              </th>
              <th className="border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold text-[#333]">
                Email
              </th>
              <th className="border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold text-[#333]">
                Role
              </th>
              <th className="border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold text-[#333]">
                Assignments
              </th>
              <th className="border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold text-[#333]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, index) => (
              <tr
                key={user.email}
                className={index % 2 === 0 ? "bg-[#ebeee5]" : "bg-white"}
              >
                <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-semibold">
                  {user.name}
                </td>
                <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] text-[#555]">
                  {user.email}
                </td>
                <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh]">
                  <span
                    className={`inline-block px-[0.6vw] py-[0.4vh] rounded font-semibold text-fs12 ${
                      roleColors[user.role] || "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {user.role}
                  </span>
                </td>
                <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh]">
                  <div className="flex flex-wrap gap-1">
                    {user.assignments.map((assignment) => (
                      <span
                        key={assignment}
                        className="inline-block bg-[#f0f0f0] border border-[#ddd] rounded px-[0.4vw] py-[0.2vh] text-fs12 text-[#333]"
                      >
                        {assignment}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh]">
                  <div className="flex items-center gap-[0.5vw]">
                    <button
                      onClick={() => handleEditClick(user)}
                      className="bg-white border border-[#86bc25] rounded-md px-[0.7vw] py-[0.5vh] text-fs12 font-semibold text-[#86bc25] hover:bg-[#86bc25] hover:text-white transition-all duration-200 flex items-center gap-[0.3vw] shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setUsers((prev) => prev.filter((u) => u.email !== user.email));
                        toast.success(`User "${user.name}" deleted successfully.`);
                      }}
                      className="bg-white border border-[#e53e3e] rounded-md px-[0.7vw] py-[0.5vh] text-fs12 font-semibold text-[#e53e3e] hover:bg-[#e53e3e] hover:text-white transition-all duration-200 flex items-center gap-[0.3vw] shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Access Modal */}
      <ModalPopup
        title={`Edit Access for ${editingUser?.name || ""} (${editingUser?.email || ""})`}
        visible={editModalOpen}
        handleClose={() => setEditModalOpen(false)}
      >
        <div className="flex flex-col gap-[2vh]">
          <div>
            <label className="block text-fs14 font-semibold mb-[0.5vh]">Role</label>
            <SelectPicker
              data={roleOptions.map((r) => ({ label: r, value: r }))}
              value={editRole}
              onChange={(val) => setEditRole(val || "")}
              placeholder="Select Role"
              block
              searchable={false}
            />
          </div>

          <div>
            <label className="block text-fs14 font-semibold mb-[0.5vh]">Commodity</label>
            <CheckPicker
              data={[{ label: "All", value: "All" }, ...sortCommodityFirst(commodityOptions).map((c) => ({ label: c, value: c }))]}
              value={editCommodity}
              onChange={(val) => handleCommodityChange(val)}
              placeholder="Select Commodity"
              block
              searchable={false}
            />
          </div>

          <div>
            <label className="block text-fs14 font-semibold mb-[0.5vh]">District</label>
            <CheckPicker
              data={[{ label: "All", value: "All" }, ...districtOptions.map((d) => ({ label: d, value: d }))]}
              value={editDistrict}
              onChange={(val) => handleDistrictChange(val)}
              placeholder="Select District"
              block
              searchable={false}
            />
          </div>

          <div className="flex justify-end mt-[1vh]">
            <Button label="Save" bgColor="#86bc25" onClick={handleEditSave} />
          </div>
        </div>
      </ModalPopup>
    </div>
  );
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState<Tab>("User Access");

  return (
    <div className="grow shrink-0 basis-[17%] ps-[3%] pr-[5%] pt-[0.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
      <div className="flex flex-col w-full">
        <div className="font-normal text-fs128">Settings</div>
        <div className="font-semibold text-fs16 text-[#858585]">
          Configuration & Administration
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-[2vh] w-full">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-[1.5vw] py-[1vh] rounded text-fs14 font-semibold border transition-colors ${
              activeTab === tab
                ? "bg-deloitteGreen text-white border-deloitteGreen"
                : "bg-white text-[#333] border-[#CDCDCD] hover:border-deloitteGreen"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="w-full mt-[2vh] bg-[#F5F5F5] rounded-md p-[2vw]">
        <h2 className="font-bold text-fs24">{tabContent[activeTab].title}</h2>
        <p className="text-[#858585] text-fs14 mt-[1vh]">
          {tabContent[activeTab].description}
        </p>

        {activeTab === "User Access" && <UserAccessTable />}
      </div>
    </div>
  );
};

export default Settings;
